import { useEffect, useState } from 'react';
import {
  Modal,
  Box,
  Button,
  Link,
  Multiselect,
  Select,
  SpaceBetween,
  Spinner,
} from '@amzn/awsui-components-console';
import {
  AddTasksToGroupCommand,
  GroupSummary,
  LaunchTask,
  ListGroupsCommand,
  ListGroupsCommandOutput,
  ListLaunchTasksCommand,
  ListLaunchTasksCommandOutput,
  WorkStatus,
} from '@amzn/awsdev-docs-virtual-smiley-conference-typescript-client';
import { useAuthState } from '../authentication/context';
import { callApi } from '../client';
import { AsyncErrorBar } from '../components/AsyncErrorBar';
import { paginateAll, PAGE_SIZE } from '../utils/pagination';

interface AddTasksToGroupModalProps {
  visible: boolean;
  conference: string;
  venue: string;
  preSelectedTaskIds?: string[];
  preSelectedGroupId?: string;
  onDismiss: () => void;
  onSuccess: (groupId: string) => void;
  onSwitchToCreate?: () => void;
}

const EXCLUDED_STATUSES: string[] = [
  WorkStatus.GROUPED,
  WorkStatus.DELETED,
  WorkStatus.RESOLVED,
  WorkStatus.SHIPPED,
];

export const AddTasksToGroupModal = ({
  visible,
  conference,
  venue,
  preSelectedTaskIds,
  preSelectedGroupId,
  onDismiss,
  onSuccess,
  onSwitchToCreate,
}: AddTasksToGroupModalProps) => {
  const [groups, setGroups] = useState<GroupSummary[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<any>(null);
  const [tasks, setTasks] = useState<LaunchTask[]>([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { token } = useAuthState();

  const resetForm = () => {
    setSelectedGroup(null);
    setSelectedTasks([]);
    setError(null);
  };

  const handleCancel = () => {
    resetForm();
    onDismiss();
  };

  useEffect(() => {
    if (visible && token) {
      void loadGroups();
      void loadTasks();
    }
  }, [visible, token]);

  // Pre-populate selected tasks from entry point
  useEffect(() => {
    if (preSelectedTaskIds && tasks.length > 0) {
      const preSelected = tasks
        .filter((t) => preSelectedTaskIds.includes(t.launchId))
        .map((t) => ({
          label: t.name || t.launchId,
          value: t.launchId,
          description: t.launchId,
        }));
      setSelectedTasks(preSelected);
    }
  }, [tasks, preSelectedTaskIds]);

  // Pre-select group when opened from Group Detail
  useEffect(() => {
    if (preSelectedGroupId && groups.length > 0) {
      const match = groups.find((g) => g.groupId === preSelectedGroupId);
      if (match) {
        setSelectedGroup({
          label: match.groupName,
          value: match.groupId,
          description: `${match.venue} · ${
            match.memberTaskIds?.length ?? 0
          } members`,
        });
      }
    }
  }, [groups, preSelectedGroupId]);

  const loadGroups = async () => {
    if (!token) return;
    setGroupsLoading(true);
    try {
      // Page through every result rather than the first ≤1000 (tcx-core-platforms-903 §8 F7).
      const { items, ok, truncated } = await paginateAll<GroupSummary>(
        (nextToken) =>
          callApi(
            new ListGroupsCommand({
              conference,
              venue,
              pageSize: PAGE_SIZE,
              nextToken,
            }),
            token
          ).then((response: ListGroupsCommandOutput) => ({
            items: response.groups ?? [],
            nextToken: response.nextToken,
            statusCode: response.$metadata.httpStatusCode ?? 0,
          }))
      );
      // Only commit the list if every page succeeded and the walk wasn't truncated at the cap. A
      // non-200 mid-pagination (or too many pages) must surface an error rather than silently showing
      // a partial/empty group list (tcx-core-platforms-903, finding on CR-301380769).
      if (!ok || truncated) {
        setError('Failed to load groups');
        return;
      }
      setGroups(items);
    } catch (err: any) {
      setError(err?.message || 'Failed to load groups');
    } finally {
      setGroupsLoading(false);
    }
  };

  const loadTasks = async () => {
    if (!token) return;
    setTasksLoading(true);
    try {
      // Scope server-side to (conference, venue) and page through every result rather than the
      // first ≤1000 (tcx-core-platforms-903 §8 F7 / F3).
      const { items, ok, truncated } = await paginateAll<LaunchTask>(
        (nextToken) =>
          callApi(
            new ListLaunchTasksCommand({
              conference,
              venue,
              pageSize: PAGE_SIZE,
              nextToken,
            }),
            token
          ).then((response: ListLaunchTasksCommandOutput) => ({
            items: response.tasks ?? [],
            nextToken: response.nextToken,
            statusCode: response.$metadata.httpStatusCode ?? 0,
          }))
      );

      // Only commit the list if every page succeeded and the walk wasn't truncated — a non-200
      // mid-pagination (or too many pages) must surface an error rather than silently showing a
      // partial/empty task list (tcx-core-platforms-903, finding on CR-301380769).
      if (!ok || truncated) {
        setError('Failed to load tasks');
        return;
      }

      // Defensive: the server already scopes to (conference, venue); keep the client filter so a
      // same-named venue in another conference can never leak in.
      const validTasks = items.filter(
        (t) =>
          t.conference === conference &&
          t.venue === venue &&
          !EXCLUDED_STATUSES.includes(t.workStatus)
      );
      setTasks(validTasks);
    } catch (err: any) {
      setError(err?.message || 'Failed to load tasks');
    } finally {
      setTasksLoading(false);
    }
  };

  const groupOptions = groups.map((g) => ({
    label: g.groupName,
    value: g.groupId,
    description: `${g.venue} · ${g.memberTaskIds?.length ?? 0} members`,
  }));

  const taskOptions = tasks.map((t) => ({
    label: t.name || t.launchId,
    value: t.launchId,
    description: t.launchId,
  }));

  const selectedGroupEntity = groups.find(
    (g) => g.groupId === selectedGroup?.value
  );
  const alreadyInGroup = selectedTasks.some((t: any) =>
    selectedGroupEntity?.memberTaskIds?.includes(t.value)
  );

  const isValid = selectedGroup && selectedTasks.length > 0 && !alreadyInGroup;

  const handleSubmit = async () => {
    if (!token || !isValid) return;
    setLoading(true);
    setError(null);

    const launchIds = selectedTasks.map((o: any) => o.value);

    try {
      await callApi(
        new AddTasksToGroupCommand({ groupId: selectedGroup.value, launchIds }),
        token
      );
      onSuccess(selectedGroup.value);
    } catch (err: any) {
      if (err?.statusCode === 409) {
        setError(
          'One or more tasks are already members of a group. Remove them from their current group before adding them to a new one.'
        );
      } else {
        setError(err?.message || String(err));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      visible={visible}
      onDismiss={handleCancel}
      header='Add tasks to group'
      footer={
        <Box float='right'>
          <SpaceBetween direction='horizontal' size='xs'>
            <Button variant='link' onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              variant='primary'
              onClick={handleSubmit}
              loading={loading}
              disabled={!isValid}
              data-test-id='confirm-add-tasks-button'
            >
              Add to group
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size='m'>
        <AsyncErrorBar error={error} header='Add Tasks Failed' />
        {preSelectedGroupId ? (
          <Box>
            <Box variant='awsui-key-label'>Group</Box>
            <Box>{selectedGroup?.label ?? 'Loading...'}</Box>
          </Box>
        ) : (
          <Select
            selectedOption={selectedGroup}
            onChange={({ detail }) => setSelectedGroup(detail.selectedOption)}
            options={groupOptions}
            placeholder='Select a group'
            loadingText='Loading groups'
            statusType={groupsLoading ? 'loading' : 'finished'}
            filteringType='auto'
            data-test-id='group-select'
          />
        )}
        {tasksLoading ? (
          <Spinner />
        ) : (
          <Multiselect
            selectedOptions={selectedTasks}
            onChange={({ detail }) =>
              setSelectedTasks([...detail.selectedOptions])
            }
            options={taskOptions}
            placeholder='Select tasks to add'
            filteringType='auto'
            data-test-id='add-tasks-multiselect'
          />
        )}
        {onSwitchToCreate && (
          <Box textAlign='center'>
            <Link onFollow={onSwitchToCreate}>Create a new group instead</Link>
          </Box>
        )}
      </SpaceBetween>
    </Modal>
  );
};
